import {
  ColDef,
  SuppressKeyboardEventParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { intl } from '../../../../../../i18n'
import {
  ProgramManagementGridContext,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../../../programManagement'
import { programItemRowService } from '../../../programItemRowService'
import { BulkSheetColumnDef } from '../../../../../containers/BulkSheetView/model'
import { GRAYED_OUT_STYLE } from '../../../../../containers/commons/AgGrid/cellStyle'
import { ProgramManagementStatusCellRenderer } from '../../../../../features/ProgramManagementStatusCellRenderer'
import {
  ColumnType,
  dateValueFormatter,
} from '../../../../../containers/commons/AgGrid'
import {
  DateCellEditor,
  IDateCellEditorParams,
} from '../../../../../containers/BulkSheetView/components/cellEditor/DateCellEditor'
import { dateVoService } from '../../../../../../domain/value-object/DateVO'
import { colorPalette } from '../../../../../style/colorPalette'

type RowValueGetter = ValueGetterParams<ProgramManagementRow>

// --- Basic columns ---

const codeColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.code',
  colId: 'code',
  field: 'code',
  headerName: intl.formatMessage({ id: 'programManagement.column.code' }),
  width: 100,
  editable: false,
  sortable: true,
  pinned: 'left',
  lockPosition: 'left',
}

const typeColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.type',
  colId: 'type',
  headerName: intl.formatMessage({ id: 'programManagement.column.type' }),
  width: 100,
  editable: false,
  sortable: true,
  pinned: 'left',
  lockPosition: 'left',
  valueGetter: (params: RowValueGetter) => {
    const type = params.data?.type
    if (!type) return ''
    const keyMap = {
      [PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM]: 'programManagement.type.program',
      [PROGRAM_MANAGEMENT_ROW_TYPE.GROUP]: 'programManagement.type.group',
      [PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT]: 'programManagement.type.project',
      [PROGRAM_MANAGEMENT_ROW_TYPE.WORKGROUP]:
        'programManagement.type.workgroup',
      [PROGRAM_MANAGEMENT_ROW_TYPE.PROCESS]: 'programManagement.type.process',
    } as const
    return intl.formatMessage({ id: keyMap[type] })
  },
}

const statusColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.status',
  colId: 'status',
  field: 'status',
  headerName: intl.formatMessage({ id: 'programManagement.column.status' }),
  width: 120,
  editable: false,
  sortable: true,
  pinned: 'left',
  lockPosition: 'left',
  // プロジェクト計画と同じセレクトボックス風チップで表示する（表示専用）。
  // 行タイプに応じて Program / Project / WbsItem のステータスチップを出し分ける。
  cellRenderer: ProgramManagementStatusCellRenderer,
}

const projectManagerColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.projectManager',
  colId: 'projectManager',
  headerName: intl.formatMessage({
    id: 'programManagement.column.projectManager',
  }),
  width: 120,
  editable: false,
  valueGetter: (params: RowValueGetter) => {
    if (params.data?.type !== PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT) return ''
    return params.data?.projectManager?.name ?? ''
  },
  cellStyle: params =>
    params.data?.type !== PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT
      ? GRAYED_OUT_STYLE
      : null,
}

/** 日付文字列の簡易バリデーション（YYYY-MM-DD or YYYY/MM/DD） */
const isValidDateString = (value: string): boolean => {
  if (!value) return true
  const normalized = value.replace(/\//g, '-')
  const parsed = new Date(normalized)
  return !isNaN(parsed.getTime())
}

const isScheduledDateEditable = (
  data: ProgramManagementRow | undefined
): boolean => !!data && programItemRowService.isScheduledDateEditable(data)

const scheduledStartDateColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.scheduledStartDate',
  colId: 'scheduledStartDate',
  headerName: intl.formatMessage({
    id: 'programManagement.column.scheduledStartDate',
  }),
  width: 110,
  // ColumnType.date で一覧共通仕様（セルコピペ・日付パース/フィルタ等）を有効化。
  // type の cellEditor は uiMeta 必須のため、自己完結の DateCellEditor で上書きする。
  type: [ColumnType.date],
  cellEditor: DateCellEditor,
  editable: params => isScheduledDateEditable(params.data),
  sortable: true,
  // 編集可能行では Delete/Backspace による値クリア（レンジ削除）を許可する
  // （defaultColDef は非編集時の Delete/Backspace を一律抑止しているため上書き）
  suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
    if (params.editing && params.event.isComposing) return true
    if (!params.editing && ['Delete', 'Backspace'].includes(params.event.key)) {
      return !isScheduledDateEditable(params.data)
    }
    return false
  },
  // 配下からの集計で補完された予定日は薄グレー文字で表示する
  cellStyle: params => ({
    justifyContent: 'left',
    ...(params.data?.scheduledStartDateAggregated && {
      color: colorPalette.monotone[3],
    }),
  }),
  valueGetter: (params: RowValueGetter) =>
    params.data?.scheduledDate?.startDate ?? '',
  valueSetter: (params: ValueSetterParams<ProgramManagementRow>) => {
    if (!params.data) return false
    const newValue = params.newValue as string
    if (!isValidDateString(newValue)) return false
    const normalized = newValue ? newValue.replace(/\//g, '-') : undefined
    // 補完値（自身の値は未設定）のクリアは「未設定のまま」＝変更なし扱いにし、
    // フォーカスアウト後も補完値の表示を維持する
    if (!normalized && params.data.scheduledStartDateAggregated) return false
    params.data.scheduledDate = {
      ...params.data.scheduledDate,
      startDate: normalized || undefined,
    }
    params.data.edited = true
    params.data.scheduledDateEdited = true
    params.data.scheduledStartDateAggregated = false
    return true
  },
  valueFormatter: params =>
    dateValueFormatter(
      params.value,
      (params.context as ProgramManagementGridContext)?.dateFormat
    ) || '',
  cellRendererParams: {
    validate: (
      value: string,
      params: { data: ProgramManagementRow }
    ): string | undefined => {
      if (!value || !params.data?.scheduledDate?.endDate) return undefined
      const start = new Date(value.replace(/\//g, '-'))
      const end = new Date(
        params.data.scheduledDate.endDate.replace(/\//g, '-')
      )
      if (start > end) {
        return intl.formatMessage({
          id: 'programManagement.validation.startDateAfterEndDate',
        })
      }
      return undefined
    },
  },
}

const scheduledEndDateColumn: BulkSheetColumnDef<ProgramManagementRow> = {
  externalId: 'programManagement.scheduledEndDate',
  colId: 'scheduledEndDate',
  headerName: intl.formatMessage({
    id: 'programManagement.column.scheduledEndDate',
  }),
  width: 110,
  type: [ColumnType.date],
  cellEditor: DateCellEditor,
  cellEditorParams: {
    // 終了日が未設定の場合、カレンダーの初期位置を開始日に合わせる
    getInitialValueOnCalendar: (params: IDateCellEditorParams) => {
      const startDate = (params.data as ProgramManagementRow)?.scheduledDate
        ?.startDate
      return startDate ? dateVoService.construct(startDate) : undefined
    },
  },
  editable: params => isScheduledDateEditable(params.data),
  sortable: true,
  // 編集可能行では Delete/Backspace による値クリア（レンジ削除）を許可する
  // （defaultColDef は非編集時の Delete/Backspace を一律抑止しているため上書き）
  suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
    if (params.editing && params.event.isComposing) return true
    if (!params.editing && ['Delete', 'Backspace'].includes(params.event.key)) {
      return !isScheduledDateEditable(params.data)
    }
    return false
  },
  // 配下からの集計で補完された予定日は薄グレー文字で表示する
  cellStyle: params => ({
    justifyContent: 'left',
    ...(params.data?.scheduledEndDateAggregated && {
      color: colorPalette.monotone[3],
    }),
  }),
  valueGetter: (params: RowValueGetter) =>
    params.data?.scheduledDate?.endDate ?? '',
  valueSetter: (params: ValueSetterParams<ProgramManagementRow>) => {
    if (!params.data) return false
    const newValue = params.newValue as string
    if (!isValidDateString(newValue)) return false
    const normalized = newValue ? newValue.replace(/\//g, '-') : undefined
    // 補完値（自身の値は未設定）のクリアは「未設定のまま」＝変更なし扱いにし、
    // フォーカスアウト後も補完値の表示を維持する
    if (!normalized && params.data.scheduledEndDateAggregated) return false
    params.data.scheduledDate = {
      ...params.data.scheduledDate,
      endDate: normalized || undefined,
    }
    params.data.edited = true
    params.data.scheduledDateEdited = true
    params.data.scheduledEndDateAggregated = false
    return true
  },
  valueFormatter: params =>
    dateValueFormatter(
      params.value,
      (params.context as ProgramManagementGridContext)?.dateFormat
    ) || '',
  cellRendererParams: {
    validate: (
      value: string,
      params: { data: ProgramManagementRow }
    ): string | undefined => {
      if (!value || !params.data?.scheduledDate?.startDate) return undefined
      const start = new Date(
        params.data.scheduledDate.startDate.replace(/\//g, '-')
      )
      const end = new Date(value.replace(/\//g, '-'))
      if (start > end) {
        return intl.formatMessage({
          id: 'programManagement.validation.startDateAfterEndDate',
        })
      }
      return undefined
    },
  },
}

export const basicColumns: ColDef<ProgramManagementRow>[] = [
  codeColumn,
  typeColumn,
  statusColumn,
  projectManagerColumn,
  scheduledStartDateColumn,
  scheduledEndDateColumn,
]
